"use client";

import { createT } from "@/app/[lang]/_utils/create-t";
import { langNames, langs, type Lang } from "@/langs";
import { Button } from "@borderless/ui/components/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@borderless/ui/components/dropdown-menu";
import { Spinner } from "@borderless/ui/components/spinner";
import { ChevronDown } from "lucide-react";
import { usePathname, useSearchParams } from "next/navigation";
import { Suspense, useState, type FC } from "react";
import { setLangAndRedirect } from "./set-lang-and-redirect";
import dictionary from "./t.json";

const t = createT(dictionary);

export const LanguageSwitcher: FC<{
  lang: Lang;
  className?: string;
}> = (props) => {
  // useSearchParams needs to be wrapped in Suspense
  return (
    <Suspense>
      <LanguageSwitcherContent {...props} />
    </Suspense>
  );
};

const LanguageSwitcherContent: FC<{
  lang: Lang;
  className?: string;
}> = (props) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [isOpen, setIsOpen] = useState(false);
  const [redirectingTo, setRedirectingTo] = useState<Lang | null>(null);

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen} modal>
      <DropdownMenuTrigger asChild className={props.className}>
        <Button onClick={() => setIsOpen(!isOpen)} size="sm" variant="ghost">
          {redirectingTo ? (
            <>
              {langNames[redirectingTo]} <Spinner />
            </>
          ) : (
            <>
              {langNames[props.lang]} <ChevronDown />
            </>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuRadioGroup
          value={props.lang}
          onValueChange={async (value) => {
            setRedirectingTo(value as Lang);
            await setLangAndRedirect.bind(null, {
              lang: value as Lang,
              pathname,
              searchParamsString: searchParams.toString(),
            })();
          }}
        >
          {langs.map((lang) => (
            <DropdownMenuRadioItem key={lang} value={lang}>
              {langNames[lang]}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
